import type { SolutionPageContent } from '../../types/solution'

export const manageMultipleLocationsSolution: SolutionPageContent = {
  slug: 'manage-multiple-locations',
  hero: {
    headline: 'Run every branch as if you were standing in it',
    subheadline: 'See sales, stock, and staff across all your locations from one workspace.',
    card: {
      type: 'progress',
      title: 'Today · all branches',
      badge: { label: 'RWF 1.62M', tone: 'brand' },
      rows: [
        { label: 'Remera', value: 'RWF 720,000', pct: 100, tone: 'positive' },
        { label: 'Kimironko', value: 'RWF 540,000', pct: 75 },
        { label: 'Nyamirambo', value: 'RWF 360,000', pct: 50 },
      ],
    },
  },
  challenges: [
    {
      quote: 'I have to visit each branch to know how it’s doing.',
      detail: 'Half my week is spent in traffic between shops.',
    },
    {
      quote: 'One branch runs out while another has too much.',
      detail: 'Stock sits in the wrong place while customers leave empty-handed.',
    },
    {
      quote: 'I can’t compare branches fairly.',
      detail: 'Every manager keeps records differently, so nothing adds up.',
    },
  ],
  helps: [
    {
      solves: 'I have to visit each branch',
      title: 'Every branch, live on your phone',
      copy: 'See sales and cash-up for every location as they happen, wherever you are.',
      product: 'sales-pos',
      visual: { kind: 'screen', id: 'staff-sales' },
    },
    {
      solves: 'One branch runs out while another has too much',
      title: 'Stock by location, with transfers',
      copy: 'Keep separate stock per branch and move products between them with a record of who sent and received what.',
      product: 'inventory',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Transfer #TR-0092',
          meta: 'Remera → Kimironko',
          badge: { label: 'In transit', tone: 'brand' },
          rows: [
            { label: 'Cooking oil 1L', value: '24' },
            { label: 'Rice 5kg', value: '10' },
            { label: 'Sugar 1kg', value: '36' },
          ],
          action: 'Confirm receipt',
        },
      },
    },
    {
      solves: 'I can’t compare branches fairly',
      title: 'Consolidated branch reports',
      copy: 'Every branch records the same way, so you can compare sales, profit, and stock side by side — or see the business as a whole.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Profit by branch · September',
          rows: [
            { label: 'Remera', value: 'RWF 1.1M', pct: 100, tone: 'positive' },
            { label: 'Kimironko', value: 'RWF 820K', pct: 75 },
            { label: 'Nyamirambo', value: 'RWF 480K', pct: 44, tone: 'warning' },
          ],
        },
      },
    },
  ],
  journey: {
    heading: 'How it works',
    subheading: 'Set up each branch once, then manage them all from anywhere.',
    items: [
      { title: 'Add your locations', copy: 'Create each shop, branch, or warehouse.' },
      { title: 'Assign staff and stock', copy: 'Give each team access to their own location.' },
      { title: 'Sell everywhere', copy: 'Every branch uses the same products and prices.' },
      { title: 'Compare and act', copy: 'See consolidated and per-branch reports.' },
    ],
  },
  results: [
    { value: '1 view', label: 'of sales and stock across every branch' },
    { value: '−50%', label: 'fewer trips between locations' },
    { value: '−35%', label: 'fewer stock-outs through branch transfers' },
  ],
  testimonial: {
    quote: 'With two shops, I used to spend my week driving between them. Now I check both branches from my phone before breakfast.',
    name: 'Solange Ingabire',
    role: 'Owner',
    business: 'Ingabire Fashion House',
    location: 'Remera & Kimironko, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/55a948a8-f1a2-4b85-9533-52b1fa01fe2f.jpg',
    metric: { value: '−50%', label: 'fewer trips between shops' },
  },
  products: ['sales-pos', 'inventory', 'reports'],
  plan: { name: 'Growth', audience: 'businesses with more than one location' },
  faq: [
    {
      question: 'Which plan supports multiple locations?',
      answer: 'Growth and Professional. You can also add extra locations to your plan as an add-on.',
    },
    {
      question: 'Can each branch have its own prices?',
      answer: 'Yes. Use the same product list everywhere, with optional branch-specific prices.',
    },
    {
      question: 'Can I limit staff to their own branch?',
      answer: 'Yes. User roles and permissions let you control who sees and does what at each location.',
    },
    {
      question: 'Can I move stock between branches?',
      answer: 'Yes. Create a transfer, and stock moves once the receiving branch confirms it.',
    },
    {
      question: 'Can I see the whole business in one report?',
      answer: 'Yes. Consolidated reports combine every branch, and you can drill down into any one.',
    },
  ],
  related: ['retail', 'hospitality', 'understand-performance'],
  cta: {
    headline: 'Manage every branch from one place',
    subheadline: 'Add your locations in minutes. Local support in Kigali when you need it.',
  },
}
